<template>
  <v-container fluid class="pa-0 pad-container">
    <v-row no-gutters class="pad-row">
      <v-col cols="12" class="pad-col">
        <v-card class="d-flex flex-column pad-card" flat elevation="0">
          <!-- Encabezado con búsqueda -->
          <v-card-title class="d-flex align-center py-2 px-3 bg-primary text-white">
            <v-icon dark>mdi-cart-outline</v-icon>
            <span class="text-subtitle-1 font-weight-bold ml-2">
              {{ showCategories ? "Categorías" : "Productos" }}
            </span>
            <v-spacer></v-spacer>
            <!-- NUEVO: Toggle Tabla/Tablero -->
            <v-btn
              color="success"
              large
              dark
              :to="{
                name: $route.name === 'InvoicesBoard' ? 'Invoices' : 'InvoicesBoard',
              }"
            >
              <v-icon left small>
                {{ $route.name === "InvoicesBoard" ? "mdi-table" : "mdi-view-dashboard" }}
              </v-icon>
              {{ $route.name === "InvoicesBoard" ? "Tabla" : "Tablero" }}
            </v-btn>
            <v-text-field
              v-model="searchQuery"
              append-inner-icon="mdi-magnify"
              placeholder="Nombre, SKU o código"
              dense
              hide-details
              outlined
              class="ml-2 search-field bg-white rounded-lg"
              style="max-width: 220px"
              clearable
              autofocus
              @click:append-inner="filterProducts"
              @keyup.enter="filterProducts"
              @click:clear="clearSearch"
            ></v-text-field>
          </v-card-title>

          <v-divider></v-divider>

          <!-- Contenido principal -->
          <v-card-text class="flex-grow-1 pa-0 products-container">
            <!-- Indicador de carga -->
            <div v-if="loading_status" class="text-center py-5">
              <v-progress-circular
                indeterminate
                color="primary"
                :size="50"
              ></v-progress-circular>
              <div class="mt-3 text-subtitle-1">Cargando inventario...</div>
            </div>

            <!-- Vista de categorías -->
            <div v-else-if="showCategories && !isSearching" class="category-grid">
              <v-btn
                v-for="(category, idx) in categories"
                :key="category.id"
                elevation="2"
                class="category-btn"
                :color="
                  category.id === 'all'
                    ? 'primary'
                    : categoryColors[idx % categoryColors.length]
                "
                dark
                @click="selectCategory(category)"
              >
                <span class="font-weight-medium">{{ category.name }}</span>
              </v-btn>
            </div>

            <!-- Vista de productos -->
            <div v-else>
              <!-- Botón de regresar y título de categoría -->
              <div class="d-flex align-center mb-3" v-if="!isSearching">
                <v-btn
                  text
                  color="primary"
                  small
                  class="text-primary"
                  @click="showCategories = true"
                >
                  <v-icon left>mdi-arrow-left</v-icon>
                  Volver a Categorías
                </v-btn>
                <span class="text-subtitle-2 font-weight-bold ml-2">
                  {{ selectedCategory ? selectedCategory.name : "" }}
                </span>
              </div>

              <!-- Estado vacío -->
              <div
                v-if="filteredProducts.length === 0"
                class="text-center py-8 my-4 rounded bg-grey-lighten-4"
              >
                <v-icon size="x-large" color="grey" class="mb-3">mdi-cart-off</v-icon>
                <div class="text-h6 text-grey-darken-2 font-weight-bold">
                  {{
                    isSearching
                      ? "No se encontraron productos"
                      : "Sin productos disponibles"
                  }}
                </div>
                <div class="text-body-2 text-grey-darken-1">
                  {{
                    isSearching
                      ? "Intente con otra búsqueda"
                      : "Seleccione otra categoría"
                  }}
                </div>
              </div>

              <!-- Grid de productos -->
              <div v-else class="product-grid">
                <v-card
                  v-for="product in paginatedProducts"
                  :key="product.sku"
                  class="product-card"
                  elevation="2"
                  :color="product.cantidad <= 0 ? 'grey lighten-3' : 'blue-grey darken-1'"
                  dark
                  @click="selectProduct(product)"
                  :disabled="product.cantidad <= 0"
                >
                  <v-card-text class="product-card-content pb-0">
                    <div class="product-info text-center">
                      <div class="product-name font-weight-medium">
                        {{ product.nombre }}
                      </div>
                    </div>
                  </v-card-text>
                  <v-card-actions class="product-card-actions justify-center pa-2">
                    <v-chip color="white" class="font-weight-bold primary--text">
                      {{ formatCurrency(product.precio) }}
                    </v-chip>
                  </v-card-actions>
                </v-card>
              </div>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <!-- Paginación (solo en vista de productos) -->
          <v-card-actions class="d-flex justify-center py-2" v-if="!showCategories">
            <v-pagination
              v-if="totalPages > 1"
              v-model="currentPage"
              :length="totalPages"
              :total-visible="isMobile ? 5 : 7"
              circle
              color="primary"
            ></v-pagination>
          </v-card-actions>

          <!-- Snackbar -->
          <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2000" top>
            <div class="d-flex align-center">
              <v-icon left class="mr-2">
                {{
                  snackbar.color === "success" ? "mdi-check-circle" : "mdi-alert-circle"
                }}
              </v-icon>
              {{ snackbar.text }}
            </div>
            <template v-slot:action="{ attrs }">
              <v-btn text color="white" v-bind="attrs" @click="snackbar.show = false"
                >Cerrar</v-btn
              >
            </template>
          </v-snackbar>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { webserver } from "@/services/webserver.js";

export default {
  name: "ProductPad",
  props: {
    locationId: {
      type: [Number, String],
      default: () => (window.store ? window.store.store_id : "00000000"),
    },
    itemsPerPage: {
      type: Number,
      default: 50,
    },
    height: {
      type: String,
      default: "100%",
    },
  },
  data() {
    return {
      loading_status: false,
      inventory_lst: [],
      inventory_filter: [],
      categories: [],
      searchQuery: "",
      isSearching: false,
      currentPage: 1,
      selectedCategory: null,
      showCategories: true,
      filteredProducts: [],
      snackbar: {
        show: false,
        text: "",
        color: "success",
      },
      isMobile: window.innerWidth < 600,
      categoryColors: [
        "cyan darken-1",
        "pink darken-1",
        "light-green darken-1",
        "amber darken-2",
        "deep-purple lighten-1",
        "teal darken-1",
        "blue-grey",
        "orange darken-1",
        "indigo lighten-1",
        "green darken-1",
        "red lighten-1",
        "brown lighten-1",
      ],
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.filteredProducts.length / this.itemsPerPage);
    },
    paginatedProducts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredProducts.slice(start, end);
    },
  },
  mounted() {
    this.get_inventory();

    // Detector de cambio de tamaño para responsive
    window.addEventListener("resize", this.checkIfMobile);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkIfMobile);
  },
  methods: {
    checkIfMobile() {
      this.isMobile = window.innerWidth < 600;
    },
    get_inventory() {
      this.loading_status = true;
      var qry = {
        store: window.store.store_id,
        table: "items",
      };
      qry.filters = [
        {
          field: "id_pos",
          operator: "=",
          value: window.store.store_id,
        },
        {
          field: "cantidad",
          operator: ">",
          value: 0,
        },
      ];

      webserver("get_table", qry, (data) => {
        // console.log(data);
        // data.forEach((itm) => {
        //   itm.category = window.settings["CATEGORIA"].find(
        //     (item) => item.codigo == itm.categoria
        //   ).valor;
        //   itm.groupName = window.settings["GRUPO"].find(
        //     (item) => item.codigo == itm.grupo
        //   ).valor;
        //   itm.brand = window.settings["MARCA"].find(
        //     (item) => item.codigo == itm.marca
        //   ).valor;
        //   itm.colorName = window.settings["COLOR"].find(
        //     (item) => item.codigo == itm.color
        //   ).valor;
        // });
        this.loading_status = false;
        this.inventory_lst = data;
        this.inventory_filter = data;

        // Extraer categorías únicas de la lista de productos
        this.mapCategories();
      });
    },

    mapCategories() {
      console.log("Mapeando categorías...", window.settings["CATEGORIA"]);
      const catSettings = (window.settings && window.settings["CATEGORIA"]) || [];

      // Filtra solo los elementos donde valor !== codigo
      const filteredCatSettings = catSettings.filter(
        (c) => c.valor && c.codigo && c.valor !== c.codigo
      );

      const uniqueCategories = [
        ...new Set(
          this.inventory_lst
            .map((item) => item.categoria)
            .filter((c) => c !== null && c !== undefined && c !== "")
        ),
      ];

      this.categories = uniqueCategories.map((code) => {
        // Busca primero el descriptivo, si no existe, toma el primero
        const found =
          filteredCatSettings.find((c) => c.codigo == code) ||
          catSettings.find((c) => c.codigo == code);
        const name = found ? found.valor || found.nombre || found.name || code : code;
        return { id: code, name };
      });

      this.categories.unshift({ id: "all", name: "Todos" });

      if (
        this.selectedCategory &&
        !this.categories.find((c) => c.id === this.selectedCategory.id)
      ) {
        this.selectedCategory = null;
        this.showCategories = true;
      }
    },

    getColorForCategory(categoryId) {
      // Genera un color consistente basado en el ID de categoría
      if (categoryId === "all") return "primary";

      // Colores predefinidos que se ven bien en la interfaz
      const colors = [
        "red",
        "pink",
        "purple",
        "deep-purple",
        "indigo",
        "blue",
        "light-blue",
        "cyan",
        "teal",
        "green",
        "light-green",
        "lime",
        "amber",
        "orange",
        "deep-orange",
        "brown",
        "blue-grey",
      ];

      // Crea un hash simple del string para seleccionar un color consistente
      let hash = 0;
      for (let i = 0; i < categoryId.length; i++) {
        hash = categoryId.charCodeAt(i) + ((hash << 5) - hash);
      }

      // Selecciona un color basado en el hash
      const index = Math.abs(hash) % colors.length;
      return colors[index];
    },

    selectCategory(category) {
      this.selectedCategory = category;
      this.showCategories = false;
      if (this.isSearching) {
        this.searchQuery = "";
        this.isSearching = false;
      }
      this.filterProductsByCategory();
      this.currentPage = 1;
    },

    filterProductsByCategory() {
      if (!this.selectedCategory) return;
      if (this.selectedCategory.id === "all") {
        this.filteredProducts = [...this.inventory_lst];
      } else {
        this.filteredProducts = this.inventory_lst.filter(
          (product) => product.categoria === this.selectedCategory.id
        );
      }
    },

    filterProducts() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) {
        this.isSearching = false;
        if (this.selectedCategory) this.filterProductsByCategory();
        return;
      }
      this.isSearching = true;
      this.showCategories = false;
      this.filteredProducts = this.inventory_lst.filter((product) =>
        [product.nombre, product.sku, product.refe, product.ean].some(
          (field) => field && field.toString().toLowerCase().includes(query)
        )
      );
      this.currentPage = 1;
    },

    clearSearch() {
      this.searchQuery = "";
      this.isSearching = false;
      if (this.selectedCategory) this.filterProductsByCategory();
      else this.showCategories = true;
      this.currentPage = 1;
    },

    selectProduct(product) {
      //console.log("Producto seleccionado:", product);
      product.cantidad = 1;
      this.$emit("product-selected", {
        ...product,
        qty: 1,
        tItem: product.price * this.qty,
      });
      //   this.$emit("product-select", product);
      //   if (parseInt(product.cantidad) <= 0) {
      //     this.showError(`Producto agotado: ${product.nombre}`);
      //     return;
      //   }
      //   this.$emit("product-select", product);
      //   this.showSuccess(`${product.nombre} seleccionado`);
    },

    getInitials(name) {
      if (!name) return "?";
      return name
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join("");
    },

    formatCurrency(value) {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
      }).format(value);
    },

    showSuccess(text) {
      this.snackbar = { show: true, text, color: "success" };
    },

    showError(text) {
      this.snackbar = { show: true, text, color: "error" };
    },
  },
  watch: {
    locationId: {
      handler(newValue) {
        if (newValue) this.get_inventory();
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.fill-height {
  height: 100%;
}

.overflow-auto {
  overflow-y: auto;
}

/* Propagación de altura para que el grid de botones pueda hacer scroll */
.pad-container {
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

.pad-row {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

.pad-col {
  display: flex !important;
  flex-direction: column !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

.pad-card {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

/* Solo aquí permitimos scroll vertical: el grid de productos */
.products-container {
  overflow-y: auto !important;
  flex: 1 1 0 !important;
  min-height: 0 !important;
}

.category-grid,
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  padding: 12px;
}

.category-btn {
  min-height: 110px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  border-radius: 4px !important;
  padding: 10px;
  box-sizing: border-box;
  text-transform: none !important;
}

.category-btn span {
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.3;
  text-align: center;
  white-space: normal !important;
  overflow-wrap: break-word !important;
  word-break: break-word !important;
  hyphens: auto;
  width: 100%;
  max-height: 2.6em; /* 2 líneas */
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.product-card {
  position: relative;
  min-height: 120px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  border-radius: 4px !important;
  padding: 0 !important;
  box-sizing: border-box;
  overflow: hidden;
}

.product-card-content {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  padding: 12px !important;
}

.product-card-actions {
  flex: 0 0 auto;
  margin-top: auto !important;
}

.product-info {
  width: 100%;
}

.product-name {
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.3;
  text-align: center;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  width: 100%;
  max-height: 2.6em; /* 2 líneas */
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* Chip de precio: jerarquía visual reforzada */
.product-card-actions .v-chip {
  height: 28px !important;
  font-size: 0.85rem !important;
  font-weight: 700;
}

.product-price {
  margin: 2px 0;
}

.search-field {
  transition: max-width 0.3s;
}

/* Ajustes responsive */
@media (max-width: 960px) {
  .category-grid,
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 10px;
    padding: 10px;
  }
}

@media (max-width: 600px) {
  .category-grid,
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 8px;
    padding: 8px;
  }

  .category-btn {
    min-height: 95px;
    padding: 6px;
  }

  .category-btn span {
    font-size: 0.85rem;
  }

  .product-name {
    font-size: 0.85rem;
  }

  .product-card {
    min-height: 105px;
  }

  .search-field {
    max-width: 140px;
  }
}
</style>
